/**
 * Runtime-карточка выбранной фичи доски (шаблон по feature_kind + symbol_kls_id + visibility).
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { tacticalApi, type TacticalFeatureCardField } from "@/services/tactical.service";

type Props = {
  boardId: number;
  featureId: number;
  canManage: boolean;
  /** Меняется при смене MIL-символа — перечитывает шаблон. */
  symbolKlsId?: number | null;
};

function clientVisibility(
  field: TacticalFeatureCardField,
  draft: Record<string, string>,
): { hide: boolean; disable: boolean } {
  const raw = field.visibility_rules;
  if (!raw || typeof raw !== "object") return { hide: false, disable: false };
  const dependsOn = typeof raw.depends_on === "string" ? raw.depends_on.trim() : "";
  if (!dependsOn) return { hide: false, disable: false };
  const expected = "equals" in raw ? raw.equals : true;
  const currentRaw = draft[dependsOn] ?? "";
  let current: unknown = currentRaw;
  if (currentRaw === "true") current = true;
  if (currentRaw === "false") current = false;
  const match = current === expected || String(current) === String(expected);
  const effect = raw.effect === "hide" || raw.effect === "disable" || raw.effect === "enable"
    ? raw.effect
    : "enable";
  if (effect === "hide") return { hide: match, disable: false };
  if (effect === "disable") return { hide: false, disable: match };
  return { hide: false, disable: !match };
}

export function TacticalFeatureCardPanel({
  boardId,
  featureId,
  canManage,
  symbolKlsId = null,
}: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});

  const cardQuery = useQuery({
    queryKey: ["tactical", "feature-card", boardId, featureId, symbolKlsId ?? "none"],
    queryFn: () => tacticalApi.getFeatureCard(boardId, featureId),
  });

  useEffect(() => {
    const values = cardQuery.data?.values ?? {};
    const next: Record<string, string> = {};
    for (const section of cardQuery.data?.sections ?? []) {
      for (const field of section.fields) {
        const raw = values[field.field_key];
        next[field.field_key] = raw == null ? "" : String(raw);
      }
    }
    setDraft(next);
  }, [cardQuery.data]);

  const saveMut = useMutation({
    mutationFn: () => {
      const values: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(draft)) {
        values[k] = v;
      }
      return tacticalApi.putFeatureCard(boardId, featureId, values);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["tactical", "feature-card", boardId, featureId] });
      toast({ title: "Карточка сохранена" });
    },
    onError: (e: Error) =>
      toast({ title: "Карточка", description: e.message, variant: "destructive" }),
  });

  const visibleSections = useMemo(() => {
    return (cardQuery.data?.sections ?? []).map((section) => ({
      ...section,
      fields: section.fields.filter((field) => !clientVisibility(field, draft).hide),
    }));
  }, [cardQuery.data?.sections, draft]);

  return (
    <div className="mt-2 space-y-2 border-t pt-2 px-1 text-sm">
      <div className="flex items-center gap-1">
        <p className="text-xs font-medium text-muted-foreground">Карточка фичи</p>
        <SectionHelpTooltip label="Справка: карточка фичи">
          <p>
            Атрибуты по опубликованному шаблону: вид фичи и опционально MIL-символ (point). Поля
            могут скрываться или блокироваться по visibility_rules (как у ОИ).
          </p>
          <ul className="list-disc space-y-1 pl-4">
            <li>Значения — в properties.card_values, отдельно от стиля и геометрии.</li>
            <li>Скрытое обязательное поле не блокирует сохранение; required_if — условная обязательность.</li>
            <li>Конструктор правил — админка «Шаблоны карточек» → Тактика (draft).</li>
            <li>Esc / снятие выбора закрывает панель вместе с выбором фичи.</li>
          </ul>
        </SectionHelpTooltip>
      </div>
      {cardQuery.isLoading ? (
        <p className="text-xs text-muted-foreground">Загрузка…</p>
      ) : cardQuery.data?.message && !cardQuery.data.template_id ? (
        <p className="text-xs text-muted-foreground">{cardQuery.data.message}</p>
      ) : (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (canManage) saveMut.mutate();
          }}
        >
          {visibleSections.map((section) =>
            section.fields.length === 0 ? null : (
              <div key={section.code} className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">{section.name}</p>
                {section.fields.map((field) => {
                  const { disable } = clientVisibility(field, draft);
                  const readonly =
                    !canManage ||
                    field.is_readonly ||
                    field.is_effectively_readonly === true ||
                    disable;
                  // При live-toggle draft поле уже отфильтровано clientVisibility; * по статике + server effective.
                  const required =
                    Boolean(field.is_required) || field.is_effectively_required === true;
                  return (
                    <label key={field.field_key} className="block text-xs">
                      {field.label}
                      {required ? " *" : ""}
                      <Input
                        className="h-7 mt-0.5"
                        value={draft[field.field_key] ?? ""}
                        disabled={readonly}
                        onChange={(ev) =>
                          setDraft((prev) => ({
                            ...prev,
                            [field.field_key]: ev.target.value,
                          }))
                        }
                      />
                    </label>
                  );
                })}
              </div>
            ),
          )}
          {canManage && cardQuery.data?.template_id != null && (
            <Button type="submit" size="sm" className="w-full h-7" disabled={saveMut.isPending}>
              {saveMut.isPending ? "Сохранение…" : "Сохранить карточку"}
            </Button>
          )}
        </form>
      )}
    </div>
  );
}
